import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Connections.css';
import { 
  Search, 
  Users, 
  User, 
  MessageCircle, 
  Briefcase, 
  MapPin,
  Loader2,
  Clock,
  Mail,
  MoreVertical,
  UserPlus,
  ChevronDown
} from 'lucide-react';
import axios from 'axios';

export default function Connections() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [filteredConnections, setFilteredConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('grid');
  const token = localStorage.getItem("token");

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Making API request...');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/connections`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConnections(response.data);
      setFilteredConnections(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load connections');
      setConnections([]);
      setFilteredConnections([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    let filtered = [...connections];
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(conn => {
        const matches = 
          conn.user?.name?.toLowerCase().includes(searchLower) ||
          conn.user?.title?.toLowerCase().includes(searchLower) ||
          conn.user?.company?.toLowerCase().includes(searchLower) ||
          conn.user?.location?.toLowerCase().includes(searchLower);
        return matches;
      });
    }
    filtered.sort((a, b) => {
      let comparison;
      switch (sortBy) {
        case 'name':
          comparison = (a.user?.name || '').localeCompare(b.user?.name || '');
          break;
        case 'company':
          comparison = (a.user?.company || '').localeCompare(b.user?.company || '');
          break;
        case 'recent':
        default:
          comparison = new Date(b.connectedAt) - new Date(a.connectedAt);
      }
      return comparison;
    });
    setFilteredConnections(filtered);
  }, [searchTerm, sortBy, connections]);

  const handleUserClick = (userId) => {
    if (userId) {
      navigate(`/connections/${userId}`);
    }
  };

  const handleMessage = (userId, e) => {
    e.stopPropagation();
    navigate(`/messages/${userId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    let formatted;
    if (diffDays === 1) formatted = 'Yesterday';
    else if (diffDays < 7) formatted = `${diffDays} days ago`;
    else if (diffDays < 30) formatted = `${Math.floor(diffDays / 7)} weeks ago`;
    else formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return formatted;
  };
  if (loading) {
    console.log('Rendering loading state');
    return (
      <div className="conn-loading">
        <Loader2 size={48} className="conn-spin" />
        <p>Loading your connections...</p>
      </div>
    );
  }
  return (
    <div className="conn-container">
      <div className="conn-header">
        <div className="conn-header-top">
          <div className="conn-title-wrapper">
            <Users size={28} className="conn-title-icon" />
            <h1 className="conn-title">My Connections</h1>
          </div>
          <span className="conn-count">
            {connections.length} {connections.length === 1 ? 'Connection' : 'Connections'}
          </span>
        </div>
        <div className="conn-filters">
          <div className="conn-search-wrapper">
            <Search size={18} className="conn-search-icon" />
            <input
              type="text"
              placeholder="Search by name, title, company..."
              value={searchTerm}
              onChange={(e) => {
                console.log('Search input changed:', e.target.value);
                setSearchTerm(e.target.value);
              }}
              className="conn-search-input"
            />
          </div>
          <div className="conn-filter-actions">
            <div className="conn-sort-wrapper">
              <select 
                className="conn-sort-select"
                value={sortBy}
                onChange={(e) => {
                  console.log('Sort changed to:', e.target.value);
                  setSortBy(e.target.value);
                }}
              >
                <option value="recent">Most Recent</option>
                <option value="name">Name</option>
                <option value="company">Company</option>
              </select>
              <ChevronDown size={14} className="conn-sort-icon" />
            </div>
            <div className="conn-view-toggle">
              <button 
                className={`conn-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => {
                  console.log('View mode changed to grid');
                  setViewMode('grid');
                }}
                title="Grid view"
              >
                <div className="conn-grid-icon">
                  <span></span><span></span><span></span>
                  <span></span><span></span><span></span>
                </div>
              </button>
              <button 
                className={`conn-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => {
                  console.log('View mode changed to list');
                  setViewMode('list');
                }}
                title="List view"
              >
                <div className="conn-list-icon">
                  <span></span><span></span><span></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
      {error && (
        <div className="conn-error">
          <div className="conn-error-content">
            <p>{error}</p>
            <button onClick={() => {
              console.log('Retry button clicked');
              fetchConnections();
            }} className="conn-retry-btn">
              Try Again
            </button>
          </div>
        </div>
      )}
      
      {!error && filteredConnections.length === 0 && (
        <div className="conn-empty">
          {searchTerm ? (
            <>
              <Search size={48} className="conn-empty-icon" />
              <h3 className="conn-empty-title">No connections found</h3>
              <p className="conn-empty-text">No connections match your search "{searchTerm}"</p>
              <button onClick={() => {
                setSearchTerm('');
              }} className="conn-clear-btn">
                Clear search
              </button>
            </>
          ) : (
            <>
              <Users size={48} className="conn-empty-icon" />
              <h3 className="conn-empty-title">No connections yet</h3>
              <p className="conn-empty-text">Start building your network by connecting with people</p>
              <button onClick={() => {
                console.log('Browse suggestions clicked');
                navigate('/network');
              }} className="conn-browse-btn">
                Browse Suggestions
              </button>
            </>
          )}
        </div>
      )}
      
      {!error && filteredConnections.length > 0 && (
        <>
          <div className={`conn-grid ${viewMode}`}>
            {filteredConnections.map((connection, index) => {
              console.log(`Rendering connection ${index + 1}:`, {
                key: connection.user?.id,
                name: connection.user?.name,
                viewMode
              });
              
              return (
                <div 
                  key={connection.user?.id} 
                  className={`conn-card ${viewMode}`}
                  onClick={() => handleUserClick(connection.user?.userid)}
                >
                  {viewMode === 'grid' ? (
                    <div className="conn-card-header">
                      <div className="conn-avatar-wrapper">
                        {connection.user?.profilepic ? (
                          <img 
                            src={`${process.env.REACT_APP_FILE_URL}${connection.user.profilepic}`} 
                            alt={connection.user.name}
                            className="conn-avatar"
                            onLoad={() => console.log(`Image loaded for ${connection.user?.name}`)}
                            onError={(e) => console.error(`Image failed to load for ${connection.user?.name}:`, e)}
                          />
                        ) : (
                          <div className="conn-avatar-placeholder">
                            <User size={32} />
                          </div>
                        )}
                      </div>                    
                      <div className="conn-card-actions">
                        <button 
                          className="conn-action-btn conn-message-btn"
                          onClick={(e) => handleMessage(connection.user?.userid, e)}
                          title="Send message"
                        >
                          <MessageCircle size={16} />
                        </button>
                        <button 
                          className="conn-action-btn conn-more-btn"
                          onClick={(e) => {
                            console.log('More options clicked for:', connection.user?.name);
                            e.stopPropagation();
                          }}
                          title="More options"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="conn-list-header">
                      <div className="conn-list-avatar-wrapper">
                        {connection.user?.profilepic ? (
                          <img 
                            src={`${process.env.REACT_APP_FILE_URL}${connection.user.profilepic}`} 
                            alt={connection.user.name}
                            className="conn-list-avatar"
                            onLoad={() => console.log(`List image loaded for ${connection.user?.name}`)}
                            onError={(e) => console.error(`List image failed to load for ${connection.user?.name}:`, e)}
                          />
                        ) : (
                          <div className="conn-list-avatar-placeholder">
                            <User size={24} />
                          </div>
                        )}
                      </div>
                      <div className="conn-list-content">
                        <div className="conn-list-info">
                          <h3 className="conn-list-name">{connection.user?.name || 'User'}</h3>
                          {connection.user?.title && (
                            <p className="conn-list-title">
                              <Briefcase size={12} className="conn-list-icon" />
                              {connection.user.title}
                              {connection.user?.company && ` at ${connection.user.company}`}
                            </p>
                          )}
                          {connection.user?.location && (
                            <p className="conn-list-location">
                              <MapPin size={12} className="conn-list-icon" />
                              {connection.user.location}
                            </p>
                          )}
                        </div>
                        <div className="conn-list-actions">
                          <button 
                            className="conn-list-action-btn"
                            onClick={(e) => handleMessage(connection.user?.userid, e)}
                            title="Send message"
                          >
                            <MessageCircle size={16} />
                          </button>
                          <button 
                            className="conn-list-action-btn"
                            onClick={(e) => {
                              console.log('List more options clicked for:', connection.user?.name);
                              e.stopPropagation();
                            }}
                            title="More options"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {viewMode === 'grid' && (
                    <div className="conn-user-info">
                      <h3 className="conn-user-name">{connection.user?.name || 'User'}</h3>
                      {connection.user?.title && (
                        <p className="conn-user-title">
                          <Briefcase size={14} className="conn-info-icon" />
                          {connection.user.title}
                        </p>
                      )}
                      {connection.user?.company && (
                        <p className="conn-user-company">
                          at {connection.user.company}
                        </p>
                      )}
                      {connection.user?.location && (
                        <p className="conn-user-location">
                          <MapPin size={14} className="conn-info-icon" />
                          {connection.user.location}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {viewMode === 'grid' ? (
                    <div className="conn-card-footer">
                      <div className="conn-meta">
                        <Clock size={12} className="conn-meta-icon" />
                        <span>Connected {formatDate(connection.connectedAt)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="conn-list-footer">
                      <div className="conn-list-meta">
                        <Clock size={12} className="conn-list-meta-icon" />
                        <span>Connected {formatDate(connection.connectedAt)}</span>
                      </div>
                      {connection.mutualConnections > 0 && (
                        <div className="conn-list-mutual">
                          <UserPlus size={12} className="conn-list-mutual-icon" />
                          <span>{connection.mutualConnections} mutual</span>
                        </div>
                      )}
                      {connection.user?.email && (
                        <div className="conn-list-email">
                          <Mail size={12} className="conn-list-email-icon" />
                          <span className="conn-list-email-text">{connection.user.email}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="conn-footer">
            <p>
              Showing {filteredConnections.length} of {connections.length} connections
            </p>
          </div>
        </>
      )}
    </div>
  );
}